import { PushAction } from 'redux-little-router';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { Action } from '../../actions';
import { BannerTextImageActions } from '../../actions/banners-edit/banner-text-image';
import {
    ShowBannerEditRequestError,
    showBannerEditRequestError
} from '../../actions/bannersEdit';
import { changeLocation } from '../../actions/common';
import {
    closeEditPageItemNameEditor,
    showEditPageItemNameEditorErrors
} from '../../actions/editPageItemNameEditor';
import { resetGridData, updateSelection } from '../../actions/grid-screen';
import {
    COPY_INTERNAL_BANNER,
    CopyInternalBannerAction,
    GET_INTERNAL_BANNERS_FOR_EDIT_OR_CREATION,
    GetInternalBannersForEditOrCreation,
    SAVE_INTERNAL_BANNER_NAME_EDITOR_CHANGES,
    SAVE_INTERNAL_BANNERS,
    SaveInternalBannerNameEditorChangesAction,
    saveInternalBannersSuccess,
    updateInternalBannerBaseInfo,
    UpdateInternalBannerImage,
    updateInternalBannerImage,
    UpdateInternalBannersForEdit,
    updateInternalBannersForEdit,
    UPLOAD_INTERNAL_BANNER_IMAGE,
    UploadInternalBannerImage
} from '../../actions/internalBannersEdit';
import { SaveInternalAdsInput } from '../../api/banners/BannersMutationExecutor';
import { mapInternalBannersTemplateResourcesEntries } from '../../mappers/banners-edit/banner-edit-internal/banner-edit-internal';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { InternalBannerValidator } from '../../service/BannerEditValidation/InternalBannerValidator';
import {
    getBannerEditPageItemEditorId,
    getBannersToDelete
} from '../../service/BannerHelpers/BannerHelpers';
import { ApiMethodExecutingError } from '../../service/Errors';
import {
    getInternalBannerDescriptionEditorValidationResult,
    getInternalBannerDescriptionValidationResult,
    splitInternalBannersOnNewAndEdited
} from '../../service/InternalBannerHelpers/InternalBannerHelpers';
import { scrollToElementById } from '../../service/ScrollHelpers';
import { EpicDeps } from '../../service/StoreFactory';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BannerId, SaveInternalBannersResult } from '../../typings/banners';
import { AdvType } from '../../typings/domain';
import { EditedInternalBanner } from '../../typings/internalBannersEditState';
import { State } from '../../typings/state';

const scrollToDuplicateInternalBannerEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = action$ => {
    return action$.pipe(
        ofType(COPY_INTERNAL_BANNER),
        mergeMap((action: CopyInternalBannerAction) => {
            scrollToElementById(action.newBannerId);

            return EMPTY;
        })
    );
};

const getInternalBannersForEditEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(GET_INTERNAL_BANNERS_FOR_EDIT_OR_CREATION),
        mergeMap(
            (
                action: GetInternalBannersForEditOrCreation
            ): Observable<
                | PushAction
                | UpdateInternalBannersForEdit
                | ShowBannerEditRequestError
            > => {
                const {
                    editInternalBanners: {
                        campaignsIds,
                        bannersIds,
                        groupsIds,
                        isNew
                    },
                    commonData: { login }
                } = store$.value;

                if (campaignsIds.length === 0) {
                    const url = UrlHelpers.getBannersListUrl();

                    return of(
                        changeLocation(
                            url,
                            {
                                ulogin: login
                            },
                            false
                        )
                    );
                }

                let dataRequest;

                if (!isNew) {
                    dataRequest = deps.bannersDataProvider.getInternalBanners(
                        action.login,
                        bannersIds,
                        campaignsIds
                    );
                } else {
                    dataRequest = deps.groupsDataProvider.getGroupBasicDataForInternalBannerCreation(
                        action.login,
                        groupsIds[0],
                        campaignsIds[0]
                    );
                }

                return from(dataRequest).pipe(
                    mergeMap(bannersData => {
                        return from(
                            deps.bannersDataProvider.getInternalPlaceTemplates(
                                bannersData.placesIds
                            )
                        ).pipe(
                            mergeMap(placeTemplates => {
                                const entries = mapInternalBannersTemplateResourcesEntries(
                                    bannersData.banners,
                                    placeTemplates
                                );

                                return from(
                                    deps.bannersDataProvider.getInternalTemplateResources(
                                        login,
                                        entries
                                    )
                                ).pipe(
                                    mergeMap(templateResources => {
                                        return of(
                                            updateInternalBannersForEdit(
                                                bannersData,
                                                placeTemplates,
                                                templateResources
                                            )
                                        );
                                    }),
                                    catchError(
                                        (error: ApiMethodExecutingError) => {
                                            return of(
                                                showBannerEditRequestError(
                                                    error
                                                )
                                            );
                                        }
                                    )
                                );
                            })
                        );
                    })
                );
            }
        )
    );
};

const saveInternalBannersEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_INTERNAL_BANNERS),
        mergeMap(
            (): Observable<Action> => {
                const state = store$.value;
                const { login } = state.commonData;
                const {
                    editInternalBanners: { banners },
                    commonData
                } = state;

                const {
                    newBanners: add,
                    editedBanners: update
                } = splitInternalBannersOnNewAndEdited(state, banners);

                const input: SaveInternalAdsInput = {
                    add: add,
                    update: update,
                    delete: getBannersToDelete<EditedInternalBanner>(banners)
                };

                if (
                    Object.keys(input.update).length === 0 &&
                    Object.keys(input.add).length === 0 &&
                    Object.keys(input.delete).length === 0
                ) {
                    // нет баннеров на добавление/сохранение/удаление
                    const url = UrlHelpers.getBannersListUrl();
                    return of(
                        changeLocation(
                            url,
                            {
                                ulogin: login
                            },
                            false
                        )
                    );
                }
                const errors = InternalBannerValidator.findErrors(
                    input,
                    state.editInternalBanners
                );

                if (errors) {
                    return of(
                        saveInternalBannersSuccess(errors),
                        ...getDescriptionEditorsValidationResultActions(
                            state,
                            input
                        )
                    );
                }

                return from(
                    deps.bannersMutationExecutor.saveInternalAds(
                        input,
                        login,
                        commonData
                    )
                ).pipe(
                    mergeMap(
                        (
                            result: SaveInternalBannersResult
                        ): Observable<Action> => {
                            const { updated, added, deleted } = result;

                            const {
                                banners: selectedBanners
                            } = store$.value.grid.selectionInfo;

                            const deletedBannersSet = new Set(deleted.ids);
                            const existingBannersInSelection = selectedBanners.filter(
                                selectedBanner =>
                                    !deletedBannersSet.has(selectedBanner)
                            );

                            if (
                                !updated.errors.length &&
                                !added.errors.length &&
                                !deleted.errors.length
                            ) {
                                const url = UrlHelpers.getBannersListUrl();

                                return from([
                                    resetGridData(),
                                    changeLocation(
                                        url,
                                        {
                                            ulogin: login
                                        },
                                        false
                                    ),
                                    updateSelection(existingBannersInSelection)
                                ]);
                            }

                            return of(
                                ...getDescriptionEditorsValidationResultActions(
                                    state,
                                    input,
                                    true
                                ),
                                saveInternalBannersSuccess(result),
                                updateSelection(existingBannersInSelection)
                            );
                        }
                    ),
                    catchError(
                        (
                            error: ApiMethodExecutingError
                        ): Observable<Action> => {
                            return of(showBannerEditRequestError(error));
                        }
                    )
                );
            }
        )
    );
};

export const updateInternalBannerNameEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(SAVE_INTERNAL_BANNER_NAME_EDITOR_CHANGES),
        switchMap(
            (action: SaveInternalBannerNameEditorChangesAction): Action[] => {
                const state = store$.value;
                const { bannerId } = action;
                const { editPageItemNameEditor: editors } = state;
                const editorId = getBannerEditPageItemEditorId(bannerId);
                const editorData = selectEditPageItemNameEditorValue(
                    editors,
                    editorId
                );

                const validationErrors = getInternalBannerDescriptionEditorValidationResult(
                    state,
                    bannerId
                );

                if (validationErrors.length === 0) {
                    return [
                        updateInternalBannerBaseInfo(bannerId, {
                            field: 'description',
                            value: editorData.value
                        }),
                        closeEditPageItemNameEditor(editorId)
                    ];
                }

                return [
                    showEditPageItemNameEditorErrors(editorId, validationErrors)
                ];
            }
        )
    );
};

const uploadInternalBannerImageEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(UPLOAD_INTERNAL_BANNER_IMAGE),
        mergeMap(
            (
                action: UploadInternalBannerImage
            ): Observable<
                BannerTextImageActions | UpdateInternalBannerImage
            > => {
                const {
                    commonData: { login },
                    editInternalBanners: { banners }
                } = store$.value;

                const banner = banners[action.bannerId];

                if (!banner) {
                    return EMPTY;
                }
                return from(
                    deps.restApiManager.uploadAdBannerImage(
                        login,
                        AdvType.INTERNAL,
                        [action.imageToUpload]
                    )
                ).pipe(
                    mergeMap(response => {
                        const hash = response.result[0].hash;

                        return of(
                            updateInternalBannerImage(
                                action.bannerId,
                                action.resourceId,
                                hash
                            )
                        );
                    }),
                    catchError(
                        (
                            error: ApiMethodExecutingError
                        ): Observable<UpdateInternalBannerImage> => {
                            return of(
                                updateInternalBannerImage(
                                    action.bannerId,
                                    action.resourceId,
                                    '',
                                    error.getErrors()
                                )
                            );
                        }
                    )
                );
            }
        )
    );
};

const getDescriptionEditorsValidationResultActions = (
    state: State,
    input: SaveInternalAdsInput,
    autosave = false
): Action[] => {
    const { editPageItemNameEditor: editors } = state;

    return [input.add, input.update].reduce(
        (
            acc: Action[],
            bannersHash: Record<BannerId, EditedInternalBanner>
        ) => {
            Object.keys(bannersHash).forEach((bannerId: BannerId) => {
                const validationErrors = getInternalBannerDescriptionValidationResult(
                    state,
                    bannerId,
                    bannersHash[bannerId].description
                );

                if (validationErrors.length > 0) {
                    acc.push(
                        showEditPageItemNameEditorErrors(
                            getBannerEditPageItemEditorId(bannerId),
                            validationErrors
                        )
                    );
                } else if (autosave) {
                    const editorId = getBannerEditPageItemEditorId(bannerId);
                    const editorData = selectEditPageItemNameEditorValue(
                        editors,
                        editorId
                    );

                    if (editorData.isVisible) {
                        acc.push(
                            updateInternalBannerBaseInfo(bannerId, {
                                field: 'description',
                                value: editorData.value
                            }),
                            closeEditPageItemNameEditor(editorId)
                        );
                    }
                }
            });

            return acc;
        },
        []
    );
};

export {
    scrollToDuplicateInternalBannerEpic,
    getInternalBannersForEditEpic,
    saveInternalBannersEpic,
    uploadInternalBannerImageEpic
};
